// ─────────────────────────────────────────────────────────────────────────────
// Sync Rules — grouped view (Store → Module → Trigger)
//
// Wired to the live settings API. Mirrors FieldMapping.jsx's data flow but
// renders the list as collapsible per-shop accordions with side-by-side
// Create / Update / Delete trigger cells per module.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";

import {
  getSettingByType,
  upsertSetting,
  deleteSettingById,
} from "../../../APICalling/settingApi";
import {
  getShopifyWebhooks,
  deleteShopifyWebhook,
} from "../../../APICalling/shopifyApi";
import {
  SHOPIFY_SHOP_URL_HEADER_KEY,
  buildShopifyShopUrlHeader,
  EXTENSION_IDENTIFIER,
} from "../../../config/serviceConstant";
import {
  getOpenSnakErrorData,
  getOpenSnakSuccessData,
} from "../../../config/otherConstant";
import WEBHOOK_LIST from "../../../sample_json/webhook_list.json";
import {
  DIRECTION_SHOPIFY_TO_CRM,
  DIRECTION_CRM_TO_SHOPIFY,
  DIRECTION_LABELS,
  normaliseDirection,
} from "../../../config/syncDirectionConstant";
import FieldMapping from "./FieldMapping";

// ── Styling tokens ──────────────────────────────────────────────────────────
const SHOPIFY_GREEN = "#0d6e1f";

const ACTION_STYLES = {
  upsert: { bg: "#dbeafe", fg: "#1d4ed8", border: "#bfdbfe", label: "Upsert" },
  update: { bg: "#fef3c7", fg: "#92400e", border: "#fde68a", label: "Update" },
  create: { bg: "#dcfce7", fg: "#15803d", border: "#bbf7d0", label: "Create" },
  delete: { bg: "#fee2e2", fg: "#991b1b", border: "#fecaca", label: "Delete" },
};

const EVENT_STYLES = {
  Create: { bg: "#ecfdf5", fg: "#047857" },
  Update: { bg: "#fef9c3", fg: "#854d0e" },
  Delete: { bg: "#fef2f2", fg: "#b91c1c" },
};

// Map a raw setting row (camelCase / snake_case / string-schema) into a flat
// config object. Mirrors FieldMapping.jsx's mapSettingToConfig.
const mapSettingToConfig = (s) => {
  if (!s) return null;
  let schema = s.settingSchema ?? s.setting_schema ?? s.schema ?? {};
  if (typeof schema === "string") {
    try { schema = JSON.parse(schema); } catch { schema = {}; }
  }
  const id = s.settingId ?? s.setting_id ?? s.id ?? "";
  const moduleApiName =
    s.moduleApiName ?? s.module_api_name ?? schema?.module ?? "";
  return { id, ...schema, module: moduleApiName || schema?.module || "" };
};

// Bucket a trigger name into Create / Update / Delete. Tolerates the
// "_UPDATED" Shopify variant and unusual webhook suffixes.
const eventForTrigger = (trigger) => {
  const t = (trigger || "").toUpperCase();
  if (/_DELETE$/.test(t)) return "Delete";
  if (/_UPDATE(D)?$/.test(t)) return "Update";
  if (/_CREATE$/.test(t)) return "Create";
  return null;
};

const actionStyleFor = (action) =>
  ACTION_STYLES[(action || "").toLowerCase()] || ACTION_STYLES.create;

// ── Trigger cell ────────────────────────────────────────────────────────────
function TriggerCell({ cfg, event, onToggle, onDelete, onEdit }) {
  const action = actionStyleFor(cfg.recordAction);
  const eventStyle = EVENT_STYLES[event] || EVENT_STYLES.Create;
  const fieldsCount = Array.isArray(cfg.rows) ? cfg.rows.length : 0;
  // The action verb targets the destination platform, which depends on the
  // rule's sync direction: webhooks write into Zoho, CRM workflows into Shopify.
  const targetPlatform =
    normaliseDirection(cfg.direction) === DIRECTION_CRM_TO_SHOPIFY
      ? "Shopify"
      : "Zoho";

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        border: "1px solid #e4e4e7",
        borderRadius: "10px",
        bgcolor: cfg.enabled ? "#fff" : "#fafafa",
        opacity: cfg.enabled ? 1 : 0.82,
        p: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        transition: "all 0.15s",
        "&:hover": { borderColor: "#a1a1aa", boxShadow: "0 1px 3px rgba(0,0,0,.04)" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.6,
            px: "6px",
            py: "1px",
            borderRadius: "4px",
            bgcolor: eventStyle.bg,
            color: eventStyle.fg,
            textTransform: "uppercase",
          }}
        >
          {event}
        </Box>
        <Switch
          size="small"
          checked={!!cfg.enabled}
          onChange={(e) => onToggle?.(cfg.id, e.target.checked)}
        />
      </Box>

      <Typography
        sx={{
          fontFamily: "ui-monospace, Menlo, Consolas, monospace",
          fontSize: 12,
          color: "#52525b",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={cfg.trigger}
      >
        {cfg.trigger}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: "auto" }}>
        <Tooltip title={`${targetPlatform} will ${action.label.toLowerCase()} the record`} arrow>
          <Chip
            label={action.label}
            size="small"
            sx={{
              height: 20,
              fontSize: 12,
              fontWeight: 700,
              bgcolor: action.bg,
              color: action.fg,
              border: `1px solid ${action.border}`,
            }}
          />
        </Tooltip>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          <Typography sx={{ fontSize: 12, color: "#71717a", mr: 0.5, fontVariantNumeric: "tabular-nums" }}>
            {fieldsCount} fld
          </Typography>
          <Tooltip title="Edit field mapping" arrow>
            <IconButton
              size="small"
              onClick={() => onEdit?.(cfg)}
              sx={{ color: "#3f3f46", p: "3px" }}
            >
              <EditOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete mapping" arrow>
            <IconButton
              size="small"
              onClick={() => onDelete?.(cfg)}
              sx={{ color: "#b91c1c", p: "3px" }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}

// ── Module group (collapsible) ──────────────────────────────────────────────
function ModuleGroup({ mod, direction, defaultExpanded, onToggle, onDelete, onEdit }) {
  const byEvent = { Create: null, Update: null, Delete: null };
  mod.triggers.forEach((c) => {
    const evt = eventForTrigger(c.trigger);
    if (evt && !byEvent[evt]) byEvent[evt] = c;
  });
  const enabledCount = mod.triggers.filter((c) => c.enabled).length;

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={{
        border: "1px solid #e4e4e7",
        borderRadius: "12px !important",
        boxShadow: "none",
        bgcolor: "#fff",
        "&:before": { display: "none" },
        overflow: "hidden",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 1.75,
          py: 0,
          minHeight: 52,
          "& .MuiAccordionSummary-content": { my: 0.75, alignItems: "center", gap: 1 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#09090b", letterSpacing: "-0.01em" }}>
            {mod.name}
          </Typography>
          {mod.service && (
            <Chip
              label={mod.service}
              size="small"
              sx={{ height: 18, fontSize: 11, bgcolor: "#eff6ff", color: "#1d4ed8" }}
            />
          )}
          <Typography sx={{ fontSize: 12, color: "#71717a", ml: 0.5 }}>
            {enabledCount} of {mod.triggers.length} enabled
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 1.75, pt: 0 }}>
        <Box sx={{ display: "flex", gap: 1.25, flexWrap: { xs: "wrap", md: "nowrap" } }}>
          {["Create", "Update", "Delete"].map((evt) => {
            if (byEvent[evt]) {
              return (
                <TriggerCell
                  key={evt}
                  cfg={byEvent[evt]}
                  event={evt}
                  onToggle={onToggle}
                  onDelete={onDelete}
                  onEdit={onEdit}
                />
              );
            }
            // CRM → Shopify update/delete rules are deliberately not seeded —
            // pushing CRM edits/deletions to a live store is an explicit
            // opt-in. Tell the client how instead of a bare "Not configured".
            const isC2SOptIn =
              direction === DIRECTION_CRM_TO_SHOPIFY &&
              (evt === "Update" || evt === "Delete");
            if (isC2SOptIn) {
              return (
                <Box
                  key={evt}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    border: "1px dashed #c7d2fe",
                    borderRadius: "10px",
                    bgcolor: "#fafbff",
                    p: 1.5,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    minHeight: 92,
                    textAlign: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <AccountTreeOutlinedIcon sx={{ fontSize: 14, color: "#4338ca" }} />
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        color: "#4338ca",
                      }}
                    >
                      {evt} — via custom workflow
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 12, color: "#6366f1", lineHeight: 1.45 }}>
                    Record {evt === "Update" ? "updates" : "deletions"} aren't
                    pushed to Shopify by default. Create a Zoho CRM workflow
                    with the easyShopify custom action to enable this.
                  </Typography>
                </Box>
              );
            }
            return (
              <Box
                key={evt}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  border: "1px dashed #d4d4d8",
                  borderRadius: "10px",
                  bgcolor: "#fafafa",
                  p: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  minHeight: 92,
                  color: "#a1a1aa",
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {evt}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#a1a1aa" }}>
                  Not configured
                </Typography>
              </Box>
            );
          })}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

// ── Store section (collapsible, one tab per sync direction) ─────────────────
function StoreSection({ store, defaultExpanded, onToggle, onDelete, onEdit }) {
  // Each store's rules are split by sync direction into two tabs:
  // "Shopify to CRM" (webhook-driven) and "CRM to Shopify" (workflow-driven).
  // Default to Shopify to CRM unless the store ONLY has CRM to Shopify rules —
  // don't open onto an empty tab when the other one has content.
  const [dirTab, setDirTab] = useState(() =>
    store.directions[DIRECTION_SHOPIFY_TO_CRM].length === 0 &&
    store.directions[DIRECTION_CRM_TO_SHOPIFY].length > 0
      ? DIRECTION_CRM_TO_SHOPIFY
      : DIRECTION_SHOPIFY_TO_CRM
  );

  const allModules = [
    ...store.directions[DIRECTION_SHOPIFY_TO_CRM],
    ...store.directions[DIRECTION_CRM_TO_SHOPIFY],
  ];
  const totalRules = allModules.reduce((n, m) => n + m.triggers.length, 0);
  const enabledRules = allModules.reduce(
    (n, m) => n + m.triggers.filter((c) => c.enabled).length,
    0
  );
  const sub = store.shop.replace(/\.myshopify\.com$/, "");
  const activeModules = store.directions[dirTab];

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={{
        border: "1px solid #e4e4e7",
        borderRadius: "14px !important",
        boxShadow: "none",
        bgcolor: "#fff",
        "&:before": { display: "none" },
        overflow: "hidden",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 2,
          py: 0.5,
          minHeight: 64,
          "& .MuiAccordionSummary-content": { my: 1, alignItems: "center", gap: 1.5 },
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "8px",
            bgcolor: "#ecfdf5",
            color: SHOPIFY_GREEN,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <StorefrontOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#09090b", letterSpacing: "-0.01em" }}>
            {sub}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#71717a" }}>
            {store.shop}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", mr: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#09090b", fontVariantNumeric: "tabular-nums" }}>
            {enabledRules}/{totalRules}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#71717a" }}>rules active</Typography>
        </Box>
      </AccordionSummary>
      <Divider />
      <AccordionDetails sx={{ p: 2, bgcolor: "#fafafa" }}>
        {/* Direction tabs */}
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            mb: 1.5,
            borderBottom: "1px solid #e4e4e7",
          }}
        >
          {[DIRECTION_SHOPIFY_TO_CRM, DIRECTION_CRM_TO_SHOPIFY].map((dir) => {
            const isActive = dirTab === dir;
            const count = store.directions[dir].reduce(
              (n, m) => n + m.triggers.length,
              0
            );
            return (
              <Box
                key={dir}
                component="button"
                onClick={() => setDirTab(dir)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: "14px",
                  py: "9px",
                  fontSize: 14,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? "#0f172a" : "#64748b",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  borderBottom: isActive
                    ? "2px solid #0f172a"
                    : "2px solid transparent",
                  mb: "-1px",
                  fontFamily: "inherit",
                  whiteSpace: "nowrap",
                  transition: "color 0.15s, border-color 0.15s",
                  "&:hover": { color: "#0f172a" },
                }}
              >
                {DIRECTION_LABELS[dir]}
                <Chip
                  label={count}
                  size="small"
                  sx={{
                    height: 17,
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: isActive ? "#e0e7ff" : "#f4f4f5",
                    color: isActive ? "#3730a3" : "#71717a",
                  }}
                />
              </Box>
            );
          })}
        </Box>

        {activeModules.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              px: 2,
              border: "1px dashed #d4d4d8",
              borderRadius: "10px",
              bgcolor: "#fff",
              color: "#71717a",
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#09090b", mb: 0.5 }}>
              No {DIRECTION_LABELS[dirTab]} rules for this store yet
            </Typography>
            <Typography sx={{ fontSize: 13 }}>
              {dirTab === DIRECTION_CRM_TO_SHOPIFY
                ? "Use “Add Mapping” and pick the CRM to Shopify direction to push Zoho CRM changes out to Shopify."
                : "Use “Add Mapping” to sync Shopify events into Zoho CRM."}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {activeModules.map((m, mi) => (
              <ModuleGroup
                key={m.name}
                mod={m}
                direction={dirTab}
                defaultExpanded={mi === 0}
                onToggle={onToggle}
                onDelete={onDelete}
                onEdit={onEdit}
              />
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export default function FieldMappingGroupedDemo(props) {
  const {
    orgId,
    apiKey,
    dataCenterUrl,
    dataCenter,
    shop,
    setSnack,
  } = props;
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Drives the embedded FieldMapping wizard. id === null → open in Add mode,
  // string id → open in Edit mode pre-filled with that mapping. Nonce makes
  // back-to-back requests for the same id re-fire the modal.
  const [openMappingRequest, setOpenMappingRequest] = useState(null);

  const requestAdd = () =>
    setOpenMappingRequest({ id: null, nonce: Date.now() });
  const requestEdit = (id) =>
    setOpenMappingRequest({ id, nonce: Date.now() });

  const settingAuth = { orgId, apiKey, dataCenterUrl, shop };

  // Per-shop Shopify headers (connname = extension identifier, shopifyshopurl
  // scopes to the shop the deletion targets).
  const buildShopifyHeaders = (forShop) => ({
    orgid: orgId,
    connname: EXTENSION_IDENTIFIER,
    apikey: apiKey,
    datacenterurl: dataCenterUrl,
    datacenter: dataCenter,
    [SHOPIFY_SHOP_URL_HEADER_KEY]: buildShopifyShopUrlHeader(forShop || shop),
  });

  const fetchConfigs = async () => {
    if (!orgId || !apiKey || !dataCenterUrl) return;
    setLoading(true);
    try {
      const list = await getSettingByType(settingAuth, "systemDefined");
      const mapped = (list || []).map(mapSettingToConfig).filter(Boolean);
      setConfigs(mapped);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, apiKey, dataCenterUrl, shop]);

  // Group configs by shop → sync direction → module → list of triggers.
  // Legacy settings without a `direction` are webhook-driven, so they land
  // under Shopify2CRM (normaliseDirection).
  const grouped = useMemo(() => {
    const byShop = new Map();
    for (const cfg of configs) {
      const sShop = cfg.shop || "(unknown shop)";
      if (!byShop.has(sShop)) {
        byShop.set(sShop, {
          [DIRECTION_SHOPIFY_TO_CRM]: new Map(),
          [DIRECTION_CRM_TO_SHOPIFY]: new Map(),
        });
      }
      const byModule = byShop.get(sShop)[normaliseDirection(cfg.direction)];
      const modKey = cfg.module || "(unknown module)";
      if (!byModule.has(modKey)) {
        byModule.set(modKey, { name: modKey, service: cfg.service || "", triggers: [] });
      }
      byModule.get(modKey).triggers.push(cfg);
    }
    return Array.from(byShop.entries()).map(([shopDomain, dirs]) => ({
      shop: shopDomain,
      directions: {
        [DIRECTION_SHOPIFY_TO_CRM]: Array.from(dirs[DIRECTION_SHOPIFY_TO_CRM].values()),
        [DIRECTION_CRM_TO_SHOPIFY]: Array.from(dirs[DIRECTION_CRM_TO_SHOPIFY].values()),
      },
    }));
  }, [configs]);

  // ── Toggle enabled (optimistic + upsert) ────────────────────────────────
  const handleToggle = async (id, val) => {
    const cfg = configs.find((c) => c.id === id);
    if (!cfg) return;
    setConfigs((prev) =>
      prev.map((c) => (c.id === id ? { ...c, enabled: val } : c))
    );
    const triggerApiName =
      cfg.triggerApiName ||
      WEBHOOK_LIST.find((w) => w.value === cfg.trigger)?.apiName ||
      "";
    const result = await upsertSetting(settingAuth, {
      settingId: id,
      settingSchema: {
        shop: cfg.shop,
        trigger: cfg.trigger,
        triggerApiName,
        service: cfg.service,
        module: cfg.module,
        rows: cfg.rows || [],
        recordAction: cfg.recordAction || "create",
        upsertField: cfg.upsertField || "",
        // Preserve the sync direction — dropping it here would silently
        // reclassify a CRM2Shopify rule as webhook-driven on every toggle.
        direction: normaliseDirection(cfg.direction),
        enabled: val,
      },
      moduleApiName: cfg.module,
      settingType: "systemDefined",
    });
    if (!result) {
      // Revert on failure
      setConfigs((prev) =>
        prev.map((c) => (c.id === id ? { ...c, enabled: !val } : c))
      );
      setSnack?.(getOpenSnakErrorData("Failed to update mapping status."));
    }
  };

  // ── Delete (confirmation → webhook + setting) ───────────────────────────
  const requestDelete = (cfg) => setDeleteTarget(cfg);
  const cancelDelete = () => !deleting && setDeleteTarget(null);

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      // 1. Best-effort delete the matching Shopify webhook first. CRM2Shopify
      //    rules are workflow-driven and never register a webhook — skip.
      if (normaliseDirection(deleteTarget.direction) === DIRECTION_SHOPIFY_TO_CRM) {
        const headers = buildShopifyHeaders(deleteTarget.shop);
        try {
          const webhooks = await getShopifyWebhooks(headers);
          const list = Array.isArray(webhooks) ? webhooks : [];
          const match = list.find((wh) => {
            const addr = wh.address || "";
            if (addr.includes("setting_id=" + deleteTarget.id)) return true;
            if (wh.setting_id === deleteTarget.id) return true;
            return false;
          });
          if (match?.id) {
            await deleteShopifyWebhook(headers, match.id, deleteTarget.id);
          }
        } catch (e) {
          console.error("Failed to delete associated webhook:", e);
        }
      }

      // 2. Delete the setting row
      const ok = await deleteSettingById(settingAuth, deleteTarget.id);
      if (!ok) {
        setSnack?.(getOpenSnakErrorData("Failed to delete field mapping."));
        return;
      }
      // Optimistically drop locally + refresh from server
      setConfigs((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setSnack?.(getOpenSnakSuccessData("Field mapping deleted."));
      fetchConfigs();
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: "auto", m: -3, p: 1.5 }}>
      {/* Page header */}
      <Box sx={{ mb: 1.5, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#09090b", letterSpacing: "-0.02em" }}>
            Sync Rules
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#71717a", mt: 0.5 }}>
            Predefined sync configurations between Shopify and Zoho CRM, grouped
            by store and module.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon sx={{ fontSize: 17 }} />}
            onClick={fetchConfigs}
            disabled={loading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "#3f3f46",
              borderColor: "#e4e4e7",
              "&:hover": { borderColor: "#a1a1aa", bgcolor: "#fafafa" },
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={requestAdd}
            startIcon={<AddIcon sx={{ fontSize: 17 }} />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "#0f172a",
              boxShadow: "none",
              "&:hover": { bgcolor: "#1e293b", boxShadow: "none" },
            }}
          >
            Add Mapping
          </Button>
        </Box>
      </Box>

      {/* Loading / empty / list */}
      {loading && configs.length === 0 ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 8 }}>
          <CircularProgress size={28} sx={{ color: SHOPIFY_GREEN }} />
        </Box>
      ) : grouped.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 3,
            color: "#71717a",
            border: "1px dashed #e4e4e7",
            borderRadius: "12px",
            bgcolor: "#fff",
          }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#09090b", mb: 0.5 }}>
            No sync rules configured yet
          </Typography>
          <Typography sx={{ fontSize: 14 }}>
            Connect a Shopify store from the Stores tab to auto-create defaults,
            or add a custom mapping from the Sync Rules tab.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {grouped.map((store) => (
            <StoreSection
              key={store.shop}
              store={store}
              defaultExpanded={false}
              onToggle={handleToggle}
              onDelete={requestDelete}
              onEdit={(cfg) => requestEdit(cfg.id)}
            />
          ))}
        </Box>
      )}

      {/* Delete confirmation */}
      <Dialog
        open={!!deleteTarget}
        onClose={cancelDelete}
        PaperProps={{ sx: { borderRadius: "14px", maxWidth: 440, width: "100%", overflow: "hidden" } }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              bgcolor: "#fee2e2",
              color: "#dc2626",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1.5,
            }}
          >
            <WarningAmberOutlinedIcon sx={{ fontSize: 24 }} />
          </Box>

          <Typography sx={{ fontSize: 17, fontWeight: 700, color: "#09090b", letterSpacing: "-0.01em", mb: 0.5 }}>
            Delete this mapping?
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#52525b", lineHeight: 1.55, mb: 2 }}>
            This will remove the{" "}
            <Box component="strong" sx={{ fontWeight: 700, color: "#09090b" }}>
              {deleteTarget?.trigger}
            </Box>{" "}
            sync rule for{" "}
            <Box component="strong" sx={{ fontWeight: 700, color: "#09090b" }}>
              {(deleteTarget?.shop || "").replace(/\.myshopify\.com$/, "")}
            </Box>
            .{" "}
            {normaliseDirection(deleteTarget?.direction) === DIRECTION_CRM_TO_SHOPIFY
              ? `No further Zoho CRM changes will be pushed to Shopify for ${deleteTarget?.module}.`
              : `The associated Shopify webhook will be deleted and no further events will be synced to ${deleteTarget?.module}.`}
          </Typography>

          <Box
            sx={{
              bgcolor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "8px",
              p: "10px 12px",
              mb: 2.5,
              fontSize: 13,
              color: "#991b1b",
              lineHeight: 1.5,
            }}
          >
            Existing Zoho records won't be deleted. You can re-add this trigger
            later from the Sync Rules tab.
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={cancelDelete}
              disabled={deleting}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                color: "#3f3f46",
                borderColor: "#e4e4e7",
                "&:hover": { borderColor: "#a1a1aa", bgcolor: "#fafafa" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={confirmDelete}
              disabled={deleting}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                bgcolor: "#dc2626",
                boxShadow: "none",
                "&:hover": { bgcolor: "#b91c1c", boxShadow: "none" },
              }}
            >
              {deleting && <CircularProgress size={14} sx={{ mr: 1, color: "#fff" }} />}
              Delete mapping
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Embedded wizard — renders only the Add/Edit Dialog. We share the
          full props bundle so it has the same auth context as the standalone
          Sync Rules tab. On close, refetch our grouped config view. */}
      <FieldMapping
        {...props}
        embedded
        openMappingRequest={openMappingRequest}
        onWizardClose={fetchConfigs}
      />
    </Box>
  );
}
